import IndexPage from 'flarum/forum/components/IndexPage';
import Page from 'flarum/common/components/Page';
import ItemList from 'flarum/common/utils/ItemList';
import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';

/**
 * 考试答题页（含开考->答题->提交->成绩全流程）
 * 继承 IndexPage 保持左侧导航栏
 */
export default class ExamTakePage extends IndexPage {
  oninit(vnode) {
    Page.prototype.oninit.call(this, vnode);
    this.bodyClass = 'App--index';
    this.examId = m.route.param('id');
    this.state = 'loading';
    this.questions = [];
    this.answers = {};
    this.timeLimit = 0;
    this.timeLeft = 0;
    this.resultData = null;
    this.error = null;
    this.start();
  }

  hero() {
    const hidden = app.forum.attribute('taroExamHeroHidden') || false;
    if (hidden) return null;
    const subtitle = app.forum.attribute('taroExamHeroSubtitle') || '检验学习成果，获得塔罗师认证';
    const icon = app.forum.attribute('taroExamHeroIcon') || 'fas fa-graduation-cap';
    const color = app.forum.attribute('taroExamHeroColor') || '';
    return (
      <header className="Hero" style={color ? { '--hero-bg': color } : undefined}>
        <div className="container">
          <h1 className="Hero-title">
            <i className={icon} style={{ marginRight: '8px' }} />
            考试中心
          </h1>
          {subtitle ? <div className="Hero-subtitle">{subtitle}</div> : null}
        </div>
      </header>
    );
  }

  contentItems() {
    const items = new ItemList();
    items.add('takeContent', this.takeContent(), 100);
    return items;
  }

  onremove(vnode) {
    super.onremove(vnode);
    if (this.timer) clearInterval(this.timer);
  }

  async start() {
    try {
      const startRes = await app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/taro/exams/' + this.examId + '/start',
      });
      this.resultId = startRes.data.id;
      this.timeLimit = startRes.data.attributes.time_limit_seconds || 3600;
      this.timeLeft = this.timeLimit;

      const qRes = await app.request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/taro/exam-results/' + this.resultId + '/questions',
      });
      this.questions = qRes.data || [];
      this.grouped = this.groupQuestions(this.questions);

      this.state = 'taking';
      this.startTimer();
    } catch (e) {
      this.error = this.parseError(e);
      this.state = 'error';
    }
    m.redraw();
  }

  groupQuestions(questions) {
    const theory = [];
    const caseGroups = {};
    const materialOrder = [];
    questions.forEach((q) => {
      const a = q.attributes || {};
      if (a.question_type === 'theory') {
        theory.push(q);
      } else {
        const mid = a.material_id;
        if (!caseGroups[mid]) { caseGroups[mid] = []; materialOrder.push(mid); }
        caseGroups[mid].push(q);
      }
    });
    return { theory, caseGroups, materialOrder };
  }

  startTimer() {
    this.timer = setInterval(() => {
      this.timeLeft--;
      if (this.timeLeft <= 0) {
        clearInterval(this.timer);
        this.submit();
      }
      m.redraw();
    }, 1000);
  }

  formatTime(s) {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m + ':' + (sec < 10 ? '0' : '') + sec;
  }

  setAnswer(qId, answer) {
    this.answers[qId] = answer;
  }

  answeredCount() {
    return Object.keys(this.answers).length;
  }

  async submit() {
    if (this.state !== 'taking') return;
    if (this.timer) clearInterval(this.timer);
    this.state = 'submitting';
    m.redraw();

    try {
      const timeSpent = this.timeLimit - this.timeLeft;
      const res = await app.request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/taro/exams/' + this.examId + '/submit',
        body: {
          data: {
            attributes: {
              result_id: parseInt(this.resultId),
              answers: this.answers,
              time_spent: timeSpent,
            },
          },
        },
      });
      this.resultData = res.data.attributes;
      this.state = 'result';
    } catch (e) {
      this.error = this.parseError(e);
      this.state = 'error';
    }
    m.redraw();
  }

  parseError(e) {
    try {
      const body = JSON.parse(e.response || '{}');
      return body.errors?.[0]?.detail || e.message || '操作失败';
    } catch (_) {
      return e.message || '操作失败';
    }
  }

  takeContent() {
    if (this.state === 'loading') {
      return <div className="ExamTakePage"><LoadingIndicator /><p>正在准备试卷...</p></div>;
    }
    if (this.state === 'error') {
      return (
        <div className="ExamTakePage">
          <div className="ExamTakePage-error">
            <i className="fas fa-exclamation-triangle" />
            <p>{this.error}</p>
            <Button className="Button" onclick={() => m.route.set(app.route('exam'))}>返回考试列表</Button>
          </div>
        </div>
      );
    }
    if (this.state === 'submitting') {
      return <div className="ExamTakePage"><LoadingIndicator /><p>提交中...</p></div>;
    }
    if (this.state === 'result') {
      return this.viewResult();
    }
    return this.viewTaking();
  }

  viewTaking() {
    return (
      <div className="ExamTakePage">
        <div className={'ExamTakePage-timer' + (this.timeLeft < 60 ? ' is-urgent' : '')}>
          <i className="fas fa-clock" />
          <span>{this.formatTime(this.timeLeft)}</span>
          <span className="ExamTakePage-progress">已答 {this.answeredCount()}/{this.questions.length}</span>
        </div>

        {this.grouped.theory.length > 0 && (
          <div className="ExamSection">
            <h3 className="ExamSection-title"><i className="fas fa-book" /> 理论题（{this.grouped.theory.length}题）</h3>
            {this.grouped.theory.map((q, i) => this.viewQuestion(q, i))}
          </div>
        )}

        {this.grouped.materialOrder.map((mid, gi) => (
          <div className="ExamSection ExamSection--case" key={mid}>
            <h3 className="ExamSection-title"><i className="fas fa-file-alt" /> 案例题·材料 {gi + 1}（{this.grouped.caseGroups[mid].length}题）</h3>
            {this.grouped.caseGroups[mid].map((q, i) => this.viewQuestion(q, i))}
          </div>
        ))}

        <div className="ExamTakePage-submit">
          <Button className="Button Button--primary" onclick={() => this.submit()}>
            提交考试（已答 {this.answeredCount()}/{this.questions.length}）
          </Button>
        </div>
      </div>
    );
  }

  viewQuestion(q, index) {
    const a = q.attributes || {};
    const answered = this.answers[q.id] !== undefined;
    return (
      <div className={'ExamQuestion' + (answered ? ' is-answered' : '')} key={q.id}>
        <div className="ExamQuestion-content">{index + 1}. {a.content}</div>
        <div className="ExamQuestion-options">
          <button
            className={'ExamOption' + (this.answers[q.id] === true ? ' is-selected' : '')}
            onclick={() => { this.setAnswer(q.id, true); m.redraw(); }}
          >✓ 正确</button>
          <button
            className={'ExamOption' + (this.answers[q.id] === false ? ' is-selected' : '')}
            onclick={() => { this.setAnswer(q.id, false); m.redraw(); }}
          >✗ 错误</button>
        </div>
      </div>
    );
  }

  viewResult() {
    const r = this.resultData || {};
    const report = r.report || {};
    return (
      <div className="ExamResultPage">
        <h2 className="ExamResultPage-title">考试结果</h2>
        <div className={'ExamResultPage-score' + (r.is_passed ? ' is-passed' : ' is-failed')}>
          <div className="ExamResultPage-scoreNum">{r.score}</div>
          <div className="ExamResultPage-scoreLabel">分</div>
          <div className="ExamResultPage-passed">{r.is_passed ? '✅ 恭喜通过' : '❌ 未通过'}</div>
          <div className="ExamResultPage-detail">正确 {r.correct_count}/{r.total_count} 题</div>
        </div>

        {report.modules && report.modules.length > 0 && (
          <div className="ExamResultPage-modules">
            <h3><i className="fas fa-chart-bar" /> 知识模块掌握情况</h3>
            {report.modules.map((m) => (
              <div className="ExamModuleStat" key={m.module_code}>
                <span className="ExamModuleStat-code">{m.module_code}</span>
                <div className="ExamModuleStat-bar">
                  <div className="ExamModuleStat-fill" style={{ width: m.score + '%' }} />
                </div>
                <span className="ExamModuleStat-score">{m.correct}/{m.total} ({m.score}%)</span>
              </div>
            ))}
          </div>
        )}

        {report.certification && (
          <div className="ExamResultPage-cert">
            <h3><i className="fas fa-certificate" /> 认证情况</h3>
            <p>认证等级：{report.certification.before_level} -> <strong>{report.certification.after_level}</strong></p>
            {report.certification.cert_code && (
              <p>认证码：<code>{report.certification.cert_code}</code></p>
            )}
          </div>
        )}

        <Button className="Button Button--primary" onclick={() => m.route.set(app.route('exam'))}>
          返回考试列表
        </Button>
      </div>
    );
  }
}
